import React from 'react';
import wrap from 'wraptile';
import getOptions from '@pluto/get-options';
import {curry} from 'ramda';
import {getYearValues, COPY_RIGHT_START_YEAR, COPY_RIGHT_END_YEAR} from '@pluto/utils';
import './MrssAdditionalFields.scss';
import MrssDefaultSelector from '../../../@pluto/mrss-defaults/MrssDefaultsConnected';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@material-ui/core';
import PollingTypeSelect from '../PollingTypeSelect/PollingTypeSelectContainer';
import NewIngestCredentials from '../NewIngestCredentials/NewIngestCredentialsContainer';

export const getRightTypeFilter = curry((rightType, item) => {
    return item !== rightType;
});

export const onChangeRightTypes = wrap((rightTypesField: any, rightType: any) => {
    const {input} = rightTypesField;
    const {value, onChange} = input;

    if (value.includes(rightType)) {
        onChange(value.filter(getRightTypeFilter(rightType)));
    } else {
        onChange([...value, rightType]);
    }
});

export const onChangeCaptionsRequired = wrap((captionsRequiredField: any) => {
    captionsRequiredField.input.onChange(!captionsRequiredField.input.value);
});

export default function MrssAdditionalFieldsContainer(props: any) {
    const {
        isShown,
        ratingField,
        categoryField,
        languageField,
        teritoriesField,
        rightTypesField,
        captionsRequiredField,
        copyrightHolderField,
        copyrightYearField,
        onPollingIntervalTypeChange,
        pollingIntervalTypeField,
        isValidDate,
        pollingIntervalType,
        feedsLimitField,
        pollingIntervalValueField,
        authenticationRequiredField,
        passwordField,
        userNameField,
    } = props;

    if (!isShown) return null;

    const yearsOptions = getYearValues(COPY_RIGHT_START_YEAR, COPY_RIGHT_END_YEAR);
    const copyrightYearOptions = getOptions(yearsOptions);
    return (
        <div className="mrss-additional-fields">
            <div className="mrss-additional-fields__label">MRSS Defaults</div>
            <div className="mrss-additional-fields__wrapper">
                <div className="mrss-additional-fields__first-line">
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-rating">
                        <div className="mrss-additional-fields__field-label">Rating</div>
                        <MrssDefaultSelector
                            onChange={ratingField.input.onChange}
                            value={ratingField.input.value}
                            mrssDefaultOption="ratings"
                            variant="outlined"
                        />
                    </div>
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-category">
                        <div className="mrss-additional-fields__field-label">
                            Category
                        </div>
                        <MrssDefaultSelector
                            onChange={categoryField.input.onChange}
                            value={categoryField.input.value}
                            mrssDefaultOption="categories"
                            variant="outlined"
                        />
                    </div>
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-language">
                        <div className="mrss-additional-fields__field-label">
                            Language *
                        </div>
                        <MrssDefaultSelector
                            onChange={languageField.input.onChange}
                            value={languageField.input.value}
                            mrssDefaultOption="languages"
                            variant="outlined"
                        />
                    </div>
                </div>
                <div className="mrss-additional-fields__second-line">
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-copyright-holder">
                        <div className="mrss-additional-fields__field-label">
                            Copyright Holder
                        </div>
                        <TextField
                            value={copyrightHolderField.input.value}
                            onChange={copyrightHolderField.input.onChange}
                            variant="outlined"
                        />
                    </div>
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-copyright-year">
                        <div className="mrss-additional-fields__field-label">
                            Copyright Year
                        </div>
                        <Select
                            value={copyrightYearField.input.value}
                            onChange={copyrightYearField.input.onChange}
                            variant="outlined"
                        >
                            {copyrightYearOptions}
                        </Select>
                    </div>
                </div>
                <div className="mrss-additional-fields__third-line">
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-teritories">
                        <div className="mrss-additional-fields__field-label">
                            Territories *
                        </div>
                        <MrssDefaultSelector
                            onChange={teritoriesField.input.onChange}
                            value={teritoriesField.input.value}
                            mrssDefaultOption="territories"
                            isMulti
                            variant="outlined"
                        />
                    </div>
                </div>
                <div className="mrss-additional-fields__fourth-line">
                    <div className="mrss-additional-fields__rights-type">
                        <div className="mrss-additional-fields__rights-type-label">
                            Rights type *
                        </div>
                        <div className="mrss-additional-fields__rights-type-wrapper">
                            <div className="mrss-additional-fields__rights-type-field mrss-additional-fields__rights-type-field_linear">
                                <Checkbox
                                    checked={rightTypesField.input.value.includes(
                                        'Linear'
                                    )}
                                    onChange={onChangeRightTypes(
                                        rightTypesField,
                                        'Linear'
                                    )}
                                />
                                <div className="mrss-additional-fields__rights-type-linear-label">
                                    Linear
                                </div>
                            </div>
                            <div className="mrss-additional-fields__rights-type-field mrss-additional-fields__rights-type-field_avod">
                                <Checkbox
                                    checked={rightTypesField.input.value.includes('AVOD')}
                                    onChange={onChangeRightTypes(rightTypesField, 'AVOD')}
                                />
                                <div className="mrss-additional-fields__rights-type-avod-label">
                                    AVOD
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mrss-additional-fields__field mrss-additional-fields__field-captions-required">
                        <div className="mrss-additional-fields__field-captions-required-label">
                            Captions Required
                        </div>
                        <FormControl>
                            <RadioGroup className="captions-required_radio-group">
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={!captionsRequiredField.input.value}
                                            onChange={onChangeCaptionsRequired(
                                                captionsRequiredField
                                            )}
                                            value="false"
                                        />
                                    }
                                    label="False"
                                />
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={Boolean(
                                                captionsRequiredField.input.value
                                            )}
                                            onChange={onChangeCaptionsRequired(
                                                captionsRequiredField
                                            )}
                                            value="true"
                                        />
                                    }
                                    label="True"
                                />
                            </RadioGroup>
                        </FormControl>
                    </div>
                </div>
                <div className="mrss-additional-fields__fifth-line">
                    <div className="mrss-additional-fields__polling">
                        <div className="new-ingest-form__field new-ingest-form__field_small">
                            <div className="new-ingest-form__field-label new-ingest-form__field-label_polling">
                                Polling Interval *
                            </div>
                            <Select
                                onChange={onPollingIntervalTypeChange}
                                value={pollingIntervalTypeField.input.value}
                                variant="outlined"
                            >
                                <MenuItem value="EVERY">Every</MenuItem>
                                <MenuItem value="DAILY">Daily</MenuItem>
                                <MenuItem value="TEST">Test</MenuItem>
                            </Select>
                        </div>
                        <div className="new-ingest-form__field">
                            <PollingTypeSelect
                                isValidDate={isValidDate}
                                pollingIntervalValueField={pollingIntervalValueField}
                                pollingIntervalType={pollingIntervalType}
                                feedsLimitField={feedsLimitField}
                            />
                        </div>
                    </div>
                </div>
                <div className="mrss-additional-fields__sixth-line">
                    <div className="mrss-additional-fields__authentication-wrapper">
                        <div className="new-ingest-form__field new-ingest-form__field_small">
                            <div className="new-ingest-form__field-label new-ingest-form__field-label_authentication">
                                Authentication Required
                            </div>
                            <Select
                                onChange={authenticationRequiredField.input.onChange}
                                value={authenticationRequiredField.input.value}
                                variant="outlined"
                            >
                                {/* @ts-expect-error ts-migrate(2769) FIXME: Type 'false' is not assignable to type 'string | n... Remove this comment to see the full error message */}
                                <MenuItem value={false}>No</MenuItem>
                                {/* @ts-expect-error ts-migrate(2769) FIXME: Type 'true' is not assignable to type 'string | nu... Remove this comment to see the full error message */}
                                <MenuItem value>Yes</MenuItem>
                            </Select>
                        </div>
                        <NewIngestCredentials
                            isCredentialsShown={authenticationRequiredField.input.value}
                            passwordField={passwordField}
                            userNameField={userNameField}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
